import { useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { IconButton } from '#src/atoms/buttons/IconButton/index.tsx';
import { iconPlus } from '#src/atoms/icons/system/iconPlus.ts';
import { iconTrash } from '#src/atoms/icons/system/iconTrash.ts';
import { TextInput } from '#src/atoms/inputs/TextInput/index.tsx';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyS, HeadlineS } from '#src/atoms/typography/index.tsx';
import { Dialog } from '#src/components/dialog/index.tsx';
import { GraphqlForm, TextField } from '#src/components/form/Form/index.tsx';
import { UploadFile } from '#src/components/form/UploadFile/index.tsx';
import type { FileWithDataURL } from '#src/components/form/UploadFile/useUploadFile.ts';
import { useIntlContext } from '#src/contexts/intl/index.ts';
import type { useMutation } from '#src/hooks/graphql/useMutation.ts';

import type {
  CreateOAuthApplicationMutation,
  CreateOAuthApplicationMutationVariables,
} from '../__generated__/index.graphql.ts';

type CreateApplicationFn = ReturnType<
  typeof useMutation<
    CreateOAuthApplicationMutation,
    CreateOAuthApplicationMutationVariables
  >
>[0];

const messages = defineMessages({
  title: {
    defaultMessage: 'Request an OAuth application',
  },
  intro: {
    defaultMessage:
      'OAuth lets users “Login with Sorare” to your app. Supported flows: Authorization Code and Client Credentials. Your credentials are issued immediately.',
  },
  name: {
    defaultMessage: 'Application name',
  },
  callbackUrls: {
    defaultMessage: 'Callback URLs',
  },
  addCallbackUrl: {
    defaultMessage: 'Add a callback URL',
  },
  removeCallbackUrl: {
    defaultMessage: 'Remove this callback URL',
  },
  logo: {
    defaultMessage: 'Logo (PNG)',
  },
  uploadLogo: {
    defaultMessage: 'Upload a logo',
  },
  changeLogo: {
    defaultMessage: 'Change logo',
  },
  submit: {
    defaultMessage: 'Create application',
  },
});

const StyledGraphqlForm = styled(GraphqlForm)`
  display: flex;
  flex-direction: column;
  gap: var(--double-unit);
  padding: var(--double-unit);
`;

const LogoPreview = styled.img`
  width: calc(5 * var(--unit));
  height: calc(5 * var(--unit));
  border-radius: var(--unit);
  object-fit: cover;
`;

const parseDataURL = (dataURL: string) => {
  const [meta, data] = dataURL.split(',');
  const logoContentType = meta.match(/data:([^;]+)/)?.[1] ?? '';
  return { logoContentType, logoData: data };
};

type Props = {
  open: boolean;
  onClose: () => void;
  createApplication: CreateApplicationFn;
};

export const CreateOAuthApplicationDialog = ({
  open,
  onClose,
  createApplication,
}: Props) => {
  const { formatMessage } = useIntlContext();
  const [redirectUris, setRedirectUris] = useState<string[]>(['', '']);
  const [logo, setLogo] = useState<FileWithDataURL | null>(null);

  const close = () => {
    setRedirectUris(['', '']);
    setLogo(null);
    onClose();
  };

  const cleanedUris = redirectUris.map(uri => uri.trim()).filter(Boolean);
  const canSubmit = !!logo && cleanedUris.length > 0;

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      open={open}
      onClose={close}
      title={
        <HeadlineS as="h5" className="text-center">
          <FormattedMessage {...messages.title} />
        </HeadlineS>
      }
    >
      <StyledGraphqlForm
        onSubmit={(values, onResult) => {
          if (!logo) return;
          const { logoData, logoContentType } = parseDataURL(logo.dataURL);
          createApplication({
            variables: {
              input: {
                name: values.name,
                redirectUris: cleanedUris,
                logoData,
                logoContentType,
              },
            },
          }).then(onResult);
        }}
        onSuccess={close}
        render={(Error, SubmitButton) => (
          <>
            <BodyS color="var(--c-nd-600)">
              <FormattedMessage {...messages.intro} />
            </BodyS>
            <TextField
              name="name"
              required
              label={formatMessage(messages.name)}
            />
            <Vertical gap={1}>
              <BodyS bold>
                <FormattedMessage {...messages.callbackUrls} />
              </BodyS>
              {redirectUris.map((uri, index) => (
                <Horizontal
                  gap={1}
                  // oxlint-disable-next-line react/no-array-index-key
                  key={index}
                >
                  <TextInput
                    type="url"
                    placeholder="https://example.com/callback"
                    value={uri}
                    onChange={e =>
                      setRedirectUris(uris =>
                        uris.map((u, i) => (i === index ? e.target.value : u))
                      )
                    }
                  />
                  {redirectUris.length > 1 && (
                    <IconButton
                      type="button"
                      size="medium"
                      color="quaternary"
                      icon={iconTrash}
                      aria-label={formatMessage(messages.removeCallbackUrl)}
                      onClick={() =>
                        setRedirectUris(uris =>
                          uris.filter((_, i) => i !== index)
                        )
                      }
                    />
                  )}
                </Horizontal>
              ))}
              <div>
                <IconButton
                  type="button"
                  size="medium"
                  color="tertiary"
                  icon={iconPlus}
                  aria-label={formatMessage(messages.addCallbackUrl)}
                  onClick={() => setRedirectUris(uris => [...uris, ''])}
                />
              </div>
            </Vertical>
            <Vertical gap={1}>
              <BodyS bold>
                <FormattedMessage {...messages.logo} />
              </BodyS>
              <Horizontal gap={2}>
                {!!logo && <LogoPreview src={logo.dataURL} alt="" />}
                <UploadFile
                  name="logo"
                  type="image/png"
                  validExtensions={['.png']}
                  currentFileUrl={logo?.dataURL ?? null}
                  onChange={setLogo}
                  buttonLabel={
                    <FormattedMessage
                      {...(logo ? messages.changeLogo : messages.uploadLogo)}
                    />
                  }
                  size="md"
                />
              </Horizontal>
            </Vertical>
            <Error code />
            <SubmitButton size="large" disabled={!canSubmit}>
              <FormattedMessage {...messages.submit} />
            </SubmitButton>
          </>
        )}
      />
    </Dialog>
  );
};
